import React from 'react';
import {
  Flex,
  Text,
  TextField,
  RadioCards,
  Badge,
  IconButton,
  Icon,
  Link,
} from '@scale/scaleui-radix';
import { TrustedTagQualityType } from '@scale/shared/types/tag';
import { ProjectsAutoComplete } from './ProjectsAutoComplete';
import { WorkerSkillsAutoComplete } from './WorkerSkillsAutoComplete';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';

interface TrustedTagFormProps {
  tagName: string;
  description: string;
  qualityType: TrustedTagQualityType;
  projects: string[];
  workerSkills: string[];
  isTagNameDisabled?: boolean;
  onTagNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onQualityTypeChange: (value: TrustedTagQualityType) => void;
  onProjectsChange: (projects: string[]) => void;
  onWorkerSkillsChange: (skills: string[]) => void;
}

export const TrustedTagForm: React.FC<TrustedTagFormProps> = ({
  tagName,
  description,
  qualityType,
  projects,
  workerSkills,
  isTagNameDisabled = false,
  onTagNameChange,
  onDescriptionChange,
  onQualityTypeChange,
  onProjectsChange,
  onWorkerSkillsChange,
}) => {
  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <Flex direction="row" gap="1">
          <Text size="2" weight="medium">
            Name of tag
          </Text>
          <Text size="1" color="red">
            *
          </Text>
        </Flex>
        <TextField.Root
          disabled={isTagNameDisabled}
          placeholder="Enter tag name"
          value={tagName}
          onChange={e => onTagNameChange(e.target.value)}
        />
      </Flex>

      <Flex direction="column" gap="2">
        <Flex direction="row" gap="1">
          <Text size="2" weight="medium">
            Description
          </Text>
          {qualityType !== TrustedTagQualityType.N_A && (
            <Text size="1" color="red">
              *
            </Text>
          )}
        </Flex>
        <TextField.Root
          placeholder="Enter description"
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
        />
      </Flex>

      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Is this tag used to identify high or low quality contributors?
        </Text>
        <RadioCards.Root
          value={qualityType}
          onValueChange={value => onQualityTypeChange(value as TrustedTagQualityType)}
        >
          <RadioCards.Item value={TrustedTagQualityType.N_A}>N/A</RadioCards.Item>
          <RadioCards.Item value={TrustedTagQualityType.LOW}>Low Quality</RadioCards.Item>
          <RadioCards.Item value={TrustedTagQualityType.HIGH}>High Quality</RadioCards.Item>
        </RadioCards.Root>
      </Flex>

      {qualityType !== TrustedTagQualityType.N_A && (
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="2" className="bg-neutral-100 rounded-md p-4">
            <Text size="2">
              {<Badge color="neutral">{tagName}</Badge>} will be displayed to other operators in the{' '}
              <Link
                href="/corp/genai-ops-hub/allocation/contributor-quality"
                className="text-info-400"
                underline="hover"
                target="_blank"
                rel="noopener noreferrer"
              >
                Allocation Center
              </Link>
              .
            </Text>
            <Text size="1">
              <Link
                href="https://scale.atlassian.net/wiki/spaces/OP/pages/1448280093/Contributor+Quality+Tags"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                className="text-info-400"
              >
                <Flex direction="row" gap="1" align="center">
                  Learn More{' '}
                  <IconButton variant="ghost" size="1">
                    <Icon
                      label="Open in new tab"
                      icon={faArrowUpRightFromSquare}
                      size="1"
                      color="info"
                    />
                  </IconButton>
                </Flex>
              </Link>
            </Text>
          </Flex>
          <ProjectsAutoComplete projects={projects} onProjectsChange={onProjectsChange} />
          <WorkerSkillsAutoComplete
            workerSkills={workerSkills}
            onWorkerSkillsChange={onWorkerSkillsChange}
          />
        </Flex>
      )}
    </Flex>
  );
};
